import React, { useState, useEffect, useMemo } from 'react';
import { FaUser, FaEnvelope, FaPhone, FaCalendar, FaClock, FaUserShield, FaEdit, FaSave, FaTimes, FaServer, FaKey, FaAt, FaPaperPlane, FaIdCard, FaBuilding, FaLock, FaGlobe, FaCheckCircle, FaExclamationTriangle, FaChevronRight, FaChevronDown, FaCog, FaBell, FaShieldAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';

const API_URL = '/api';

const Profile = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [successMsg, setSuccessMsg] = useState('');
  const [smtpSettings, setSmtpSettings] = useState(null);
  const [smtpForm, setSmtpForm] = useState({ host: '', port: '', user: '', pass: '', from: '' });
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpError, setSmtpError] = useState('');
  const [smtpSuccess, setSmtpSuccess] = useState('');
  const [smtpMode, setSmtpMode] = useState('view'); // 'view' | 'edit' | 'add'
  const [activeSection, setActiveSection] = useState('profile');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const navigate = useNavigate();

  // Theme colors matching the modals
  const colors = {
    primary: '#384959',      // Main text color
    secondary: '#64748b',    // Secondary text, labels
    muted: '#A0AEC0',        // Icons and subtle elements
    accent: '#6A89A7',       // Active states and highlights (greyish-blue)
    border: '#E2E8F0',       // Card borders
    tabBorder: '#BDDDFC',    // Tab borders
    background: '#F9FAFB',   // Card backgrounds
    pageBackground: '#FFFFFF', // Main background
    success: '#10B981',      // Success color
    warning: '#F59E0B',      // Warning color
    danger: '#EF4444',       // Danger color
  };

  // --- Sidebar theme hooks (added) ---
  // Keep this defined early so loading/error UI can use it
  const pageTheme = useMemo(() => ({
    bg: '#F1F5F9',             // overall page background used in this file
    sidebarBg: '#ffffff',
    accent: colors.accent,     // greyish-blue active color (#6A89A7)
    border: colors.border,
    text: colors.primary,      // #384959
    muted: colors.secondary,
    iconColor: colors.primary,
    hoverBg: '#eff6ff',
    logout: colors.primary     // profile/logout bg
  }), [colors.accent, colors.border, colors.primary, colors.secondary]);

  useEffect(() => {
    // guard for SSR / non-browser environments
    if (typeof document === 'undefined') return;

    const themeVars = {
      '--sidebar-line-color': pageTheme.border,
      '--sidebar-bg': pageTheme.sidebarBg,
      '--sidebar-text': pageTheme.text,
      '--accent-color': pageTheme.accent,
      '--accent-hover': pageTheme.accent,
      '--page-border-color': pageTheme.border,
      '--page-bg': pageTheme.bg,
      '--text-primary': pageTheme.text,
      '--text-muted': pageTheme.muted,
      '--icon-color': pageTheme.iconColor,
      '--hover-bg': pageTheme.hoverBg,
      '--sidebar-active-text': pageTheme.accent,
      '--sidebar-profile-bg': pageTheme.logout,
      '--logout-bg': pageTheme.logout
    };

    Object.entries(themeVars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));

    const styleId = 'profile-sidebar-overrides';
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();

    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      /* Sidebar base: white background, dark text for non-active items */
      .sidebar, .app-sidebar { background-color: var(--sidebar-bg) !important; color: var(--sidebar-text) !important; }
      .sidebar a, .sidebar .menu-item, .app-sidebar a, .app-sidebar .menu-item,
      .sidebar .nav-link, .app-sidebar .nav-link { color: var(--sidebar-text) !important; background-color: #ffffff !important; }

      /* Active item: use accent background with white text & icons */
      .sidebar a.active, .sidebar .nav-link.active, .sidebar .menu-item.active, .sidebar .nav-item.active,
      .app-sidebar a.active, .app-sidebar .nav-link.active {
        background-color: var(--accent-color) !important;
        color: #ffffff !important;
        font-weight: 700;
      }

      /* Force nested text/icon inside active item to be white */
      .sidebar a.active *, .sidebar .menu-item.active *, .sidebar .nav-link.active *,
      .app-sidebar a.active *, .app-sidebar .menu-item.active *, .app-sidebar .nav-link.active * {
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
        opacity: 1 !important;
      }

      /* Common label selectors */
      .sidebar a.active .menu-text,
      .sidebar a.active .menu-item-text,
      .sidebar a.active .item-label,
      .sidebar .menu-item.active .menu-text,
      .sidebar .menu-item.active .menu-item-text,
      .sidebar .menu-item.active .item-label {
        color: #ffffff !important;
        opacity: 1 !important;
      }

      /* Ensure active icons also use white */
      .sidebar a.active svg, .sidebar .nav-link.active svg, .app-sidebar a.active svg {
        color: #ffffff !important;
        fill: #ffffff !important;
      }

      /* If sidebar used an inset bar, remove */
      .sidebar a.active::before, .app-sidebar a.active::before {
        background: transparent !important;
      }

      /* Catch-all: if sidebar uses Tailwind slate or inline color, force white for active */
      .sidebar .bg-slate-800, .sidebar .bg-slate-700,
      .app-sidebar .bg-slate-800, .app-sidebar .bg-slate-700,
      .sidebar [class*="bg-slate-800"], .sidebar [class*="bg-slate-700"],
      .app-sidebar [class*="bg-slate-800"], .app-sidebar [class*="bg-slate-700"],
      .sidebar [style*="${pageTheme.accent}"], .sidebar [style*="#374151"],
      .app-sidebar [style*="${pageTheme.accent}"], .app-sidebar [style*="#374151"],
      .sidebar [style*="background-color: var(--accent-color)"], .app-sidebar [style*="background-color: var(--accent-color)"] {
        color: #ffffff !important;
      }

      .sidebar .bg-slate-800 *, .sidebar .bg-slate-700 *,
      .app-sidebar .bg-slate-800 *, .app-sidebar .bg-slate-700 *,
      .sidebar [class*="bg-slate-800"] *, .sidebar [class*="bg-slate-700"] *,
      .app-sidebar [class*="bg-slate-800"] *, .app-sidebar [class*="bg-slate-700"] *,
      .sidebar [style*="${pageTheme.accent}"] *, .sidebar [style*="#374151"] *,
      .app-sidebar [style*="${pageTheme.accent}"] *, .app-sidebar [style*="#374151"] * {
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
        opacity: 1 !important;
      }

      /* Profile avatar / icon in sidebar: use logout bg (dark) */
      .sidebar .profile .avatar, .sidebar .profile .icon, .sidebar .profile svg,
      .app-sidebar .profile .avatar, .app-sidebar .profile .icon, .app-sidebar .profile svg {
        background-color: var(--logout-bg) !important;
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
      }

      /* Logout button style */
      .sidebar .logout-button, .app-sidebar .logout-button,
      .sidebar .profile-action, .sidebar .logout, .app-sidebar .logout,
      .sidebar .logout-btn, .app-sidebar .logout-btn {
        background-color: var(--logout-bg) !important;
        color: #ffffff !important;
        border-radius: 4px !important;
        padding: 6px 10px !important;
      }

      /* Non-active items */
      .sidebar .menu-item, .app-sidebar .menu-item, .sidebar .nav-link, .app-sidebar .nav-link {
        background-color: #ffffff !important;
        color: var(--sidebar-text) !important;
      }

      /* Hover */
      .sidebar a:hover, .app-sidebar a:hover { background-color: rgba(0,0,0,0.03) !important; }
    `;

    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [pageTheme]);
  // --- end sidebar theme hooks ---

  useEffect(() => {
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (userProfile && userProfile.Role && userProfile.Role.toLowerCase() === 'admin') {
      fetchSmtpSettings();
    }
  }, [userProfile]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/profile`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const data = await response.json();
      if (response.ok) {
        setUserProfile(data.user);
        setEditForm(data.user);
      } else {
        setError(data.message || 'Failed to fetch profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSuccessMsg('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm(userProfile);
    setSuccessMsg('');
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMsg('');
      const response = await fetch(`${API_URL}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
        body: JSON.stringify({
          Username: editForm.Username,
          FullName: editForm.FullName,
          Email: editForm.Email,
          Phone: editForm.Phone
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setUserProfile(data.user);
        setEditForm(data.user);
        setIsEditing(false);
        setSuccessMsg('Profile updated successfully!');
      } else {
        setError(data.message || 'Failed to update profile');
      }
    } catch (err) {
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // SMTP SETTINGS (Admin only)
  const fetchSmtpSettings = async () => {
    setSmtpLoading(true);
    setSmtpError('');
    setSmtpSuccess('');
    try {
      const res = await fetch(`${API_URL}/smtp-settings`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (res.ok) {
        const data = await res.json();
        if (data && data.settings) {
          setSmtpSettings(data.settings);
          setSmtpForm(data.settings);
          setSmtpMode('view');
        } else {
          setSmtpSettings(null);
          setSmtpForm({ host: '', port: '', user: '', pass: '', from: '' });
          setSmtpMode('add');
        }
      }
    } catch (err) {
      setSmtpError('Failed to fetch SMTP settings');
    } finally {
      setSmtpLoading(false);
    }
  };

  const handleSmtpInputChange = (field, value) => {
    setSmtpForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSmtpSave = async () => {
    setSmtpLoading(true);
    setSmtpError('');
    setSmtpSuccess('');
    try {
      const res = await fetch(`${API_URL}/smtp-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json','Authorization':`Bearer ${localStorage.getItem('authToken')}` },
        body: JSON.stringify(smtpForm)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSmtpSuccess('SMTP settings saved successfully!');
        setSmtpSettings(smtpForm);
        setSmtpMode('view');
      } else {
        setSmtpError(data.message || 'Failed to save SMTP settings');
      }
    } catch (err) {
      setSmtpError('Failed to save SMTP settings');
    } finally {
      setSmtpLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'bg-green-500 text-white' : 'bg-gray-500 text-white';
  };

  const getRoleColor = (role) => {
    const roleColors = {
      'admin': 'bg-purple-500 text-white',
      'user': 'bg-blue-500 text-white',
      'manager': 'bg-orange-500 text-white',
      'default': 'bg-gray-500 text-white'
    };
    return roleColors[role?.toLowerCase()] || roleColors.default;
  };

  const handleLogout = () => {
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex bg-[#F1F5F9]" style={{ backgroundColor: pageTheme.bg, '--page-border-color': pageTheme.border }}>
        <Sidebar onLogout={handleLogout} collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed} theme={{ ...pageTheme, activeBg: pageTheme.accent, activeText: '#ffffff', profileBg: pageTheme.logout, logoutBg: pageTheme.logout }} />
        <main className={`flex-1 p-8 ml-16 overflow-hidden overflow-y-auto ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
          <div className="flex items-center justify-center h-full">
            <div className="text-center animate-pulse">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-[#384959] to-[#6A89A7] rounded-full flex items-center justify-center">
                <FaUser className="text-4xl text-white" />
              </div>
              <p className="text-[#384959] text-lg font-semibold animate-bounce">Loading profile...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex bg-[#F1F5F9]" style={{ backgroundColor: pageTheme.bg, '--page-border-color': pageTheme.border }}>
        <Sidebar onLogout={handleLogout} collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed} theme={{ ...pageTheme, activeBg: pageTheme.accent, activeText: '#ffffff', profileBg: pageTheme.logout, logoutBg: pageTheme.logout }} />
        <main className={`flex-1 p-8 ml-16 overflow-y-auto ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md animate-fade-in">
              <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-6 shadow-sm animate-pulse">
                <div className="flex items-center justify-center mb-3">
                  <FaExclamationTriangle className="text-red-500 text-2xl mr-2 animate-bounce" />
                  <p className="font-bold text-lg">Error</p>
                </div>
                <p>{error}</p>
              </div>
              <button 
                onClick={fetchUserProfile}
                className="px-6 py-3 bg-[#6A89A7] text-white rounded-lg font-medium hover:bg-[#384959] transition-all duration-300 transform hover:scale-105 shadow-md"
              >
                Try Again
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#F1F5F9]" style={{ backgroundColor: pageTheme.bg, '--page-border-color': pageTheme.border }}>
      <Sidebar onLogout={handleLogout} collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed} theme={{ ...pageTheme, activeBg: pageTheme.accent, activeText: '#ffffff', profileBg: pageTheme.logout, logoutBg: pageTheme.logout }} />
      <main className={`flex-1 p-6 ml-16 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="max-w-6xl mx-auto">
          {/* Header with animated background */}
          <div className="mb-10 relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#384959] to-[#6A89A7] p-8 text-white animate-gradient-x">
            <div className="absolute inset-0 bg-black opacity-10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-4xl font-bold mb-2 animate-slide-in-left">User Profile</h1>
                  <p className="text-lg opacity-90 animate-slide-in-left animation-delay-200">Manage your account information and preferences</p>
                </div>
                {!isEditing && (
                  <button
                    onClick={handleEdit}
                    className="flex items-center space-x-2 bg-white text-[#384959] px-5 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg animate-fade-in animation-delay-400"
                  >
                    <FaEdit />
                    <span>Edit Profile</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          {/* <div className="flex space-x-1 mb-8 bg-white p-1 rounded-xl shadow-sm animate-fade-in animation-delay-300">
            {[
              { id: 'profile', label: 'Profile', icon: FaUser },
              { id: 'security', label: 'Security', icon: FaShieldAlt },
              { id: 'notifications', label: 'Notifications', icon: FaBell },
              { id: 'settings', label: 'Settings', icon: FaCog }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all duration-300 flex-1 justify-center ${
                  activeSection === tab.id 
                    ? 'bg-[#6A89A7] text-white shadow-md' 
                    : 'text-[#64748b] hover:bg-[#F9FAFB]'
                }`}
              >
                <tab.icon />
                <span>{tab.label}</span>
              </button>
            ))}
          </div> */}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Profile Card - Left Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-[1.02] transition-all duration-500 animate-slide-in-left">
                {/* Profile Header with animated gradient */}
                <div className="relative h-40 bg-gradient-to-r from-[#384959] via-[#6A89A7] to-[#384959] bg-size-200 animate-gradient-flow">
                  <div className="absolute inset-0 bg-black opacity-20"></div>
                  <div className="absolute top-4 right-4">
                    <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse"></div>
                  </div>
                </div>
                
                {/* Profile Avatar with floating animation */}
                <div className="relative px-6 -mt-16 mb-4">
                  <div className="relative group">
                    <div className="w-32 h-32 mx-auto rounded-full border-4 border-white shadow-xl overflow-hidden bg-white transform transition-transform duration-500 group-hover:scale-110">
                      {userProfile?.FullName ? (
                        <div className="w-full h-full bg-gradient-to-br from-[#384959] to-[#6A89A7] flex items-center justify-center">
                          <span className="text-white text-4xl font-bold">
                            {userProfile.FullName.split(' ').map(n => n[0]).join('').substring(0, 2)}
                          </span>
                        </div>
                      ) : (
                        <FaUser className="text-6xl text-[#A0AEC0] m-auto" />
                      )}
                    </div>
                    <div className="absolute bottom-2 right-2 w-8 h-8 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                      <FaCheckCircle className="text-white text-xs" />
                    </div>
                  </div>
                </div>
                
                {/* Profile Info */}
                <div className="text-center px-6 mb-6">
                  <h2 className="text-2xl font-bold text-[#384959] mb-1 animate-fade-in">
                    {userProfile?.FullName || 'User Name'}
                  </h2>
                  <p className="text-[#64748b] mb-3 animate-fade-in animation-delay-100 uppercase">{userProfile?.Email}</p>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRoleColor(userProfile?.Role)} animate-fade-in animation-delay-200`}>
                    {userProfile?.Role || 'User'}
                  </div>
                </div>

                {/* Profile Stats with hover effects */}
                <div className="grid grid-cols-3 gap-2 mb-6 px-6">
                  {[
                    { label: 'User ID', value: userProfile?.UserID, color: 'from-blue-500 to-blue-600' },
                    { label: 'Status', value: userProfile?.IsActive ? 'Active' : 'Inactive', color: 'from-green-500 to-green-600' },
                    { label: 'Role', value: userProfile?.Role || 'User', color: 'from-purple-500 to-purple-600' }
                  ].map((stat, index) => (
                    <div 
                      key={stat.label}
                      className="bg-[#F9FAFB] p-3 rounded-lg text-center border border-[#E2E8F0] transform transition-all duration-300 hover:scale-105 hover:shadow-md animate-fade-in-up"
                      style={{ animationDelay: `${index * 100 + 300}ms` }}
                    >
                      <div className="text-lg font-bold text-[#384959]">{stat.value}</div>
                      <div className="text-xs text-[#64748b]">{stat.label}</div>
                    </div>
                  ))}
                </div>

                {/* Contact Info with hover effects */}
                <div className="space-y-3 px-6 pb-6">
                  {[
                    { icon: FaEnvelope, label: 'Email', value: userProfile?.Email },
                    { icon: FaPhone, label: 'Phone', value: userProfile?.Phone || 'N/A' },
                    { icon: FaUserShield, label: 'Status', value: userProfile?.IsActive ? 'Active' : 'Inactive', isBadge: true }
                  ].map((item, index) => (
                    <div 
                      key={item.label}
                      className="flex items-center space-x-3 p-3 bg-[#F9FAFB] rounded-lg border border-[#E2E8F0] transform transition-all duration-300 hover:scale-[1.02] hover:shadow-md animate-fade-in-up"
                      style={{ animationDelay: `${index * 100 + 400}ms` }}
                    >
                      <div className="w-10 h-10 rounded-full bg-[#BDDDFC] flex items-center justify-center transform transition-transform duration-300 group-hover:rotate-12">
                        <item.icon className="text-[#6A89A7]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-[#64748b]">{item.label}</p>
                        {item.isBadge ? (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(userProfile?.IsActive)}`}>
                            {item.value}
                          </span>
                        ) : (
                          <p className="font-medium text-[#384959] truncate">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* account summary */}
              <div className="bg-white rounded-2xl shadow-xl p-8 animate-fade-in-up animation-delay-600 mt-8">
                <h3 className="text-2xl font-bold text-[#384959] mb-6 flex items-center gap-2">
                  <FaGlobe className="text-[#6A89A7]" />
                  Account Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-6 uppercase">
                  {[
                    { 
                      label: 'User ID', 
                      value: userProfile?.UserID,
                      gradient: 'from-[#384959] to-[#6A89A7]'
                    },
                    { 
                      label: 'Role', 
                      value: userProfile?.Role || 'User',
                      gradient: 'from-[#6A89A7] to-[#BDDDFC]'
                    },
                    { 
                      label: 'Status', 
                      value: userProfile?.IsActive ? 'Active' : 'Inactive',
                      gradient: 'from-[#BDDDFC] to-[#F1F5F9]'
                    }
                  ].map((item, index) => (
                    <div 
                      key={item.label}
                      className="text-center p-2 bg-gradient-to-br ${item.gradient} rounded-xl text-[#384959] transform transition-all duration-500 hover:scale-105 shadow-lg animate-fade-in-up"
                      style={{ animationDelay: `${index * 100 + 400}ms` }}
                    >
                      <div className="text-2xl font-bold mb-2">{item.value}</div>
                      <div className="text-sm opacity-90">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              
            </div>
            

            {/* Main Content Area */}
            <div className="lg:col-span-2 space-y-8">
              {/* Profile Details Card */}
              <div className="bg-white rounded-2xl shadow-xl p-8 animate-slide-in-right animation-delay-200">
                <div className="flex justify-between items-center mb-8">
                  <h3 className="text-2xl font-bold text-[#384959] flex items-center gap-2">
                    <FaIdCard className="text-[#6A89A7]" />
                    Account Details
                  </h3>
                  {isEditing && (
                    <div className="flex space-x-3 animate-fade-in">
                      <button
                        onClick={handleSave}
                        className="flex items-center space-x-2 bg-[#10B981] hover:bg-[#059669] text-white px-4 py-2 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md"
                      >
                        <FaSave />
                        <span>Save Changes</span>
                      </button>
                      <button
                        onClick={handleCancel}
                        className="flex items-center space-x-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-all duration-300 transform hover:scale-105"
                      >
                        <FaTimes />
                        <span>Cancel</span>
                      </button>
                    </div>
                  )}
                </div>

                {successMsg && (
                  <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center animate-fade-in-down">
                    <FaCheckCircle className="mr-2 animate-pulse" />
                    {successMsg}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { 
                      label: 'Username', 
                      icon: FaIdCard, 
                      field: 'Username',
                      value: userProfile?.Username.toUpperCase()
                    },
                    { 
                      label: 'Full Name', 
                      icon: FaUser, 
                      field: 'FullName',
                      value: userProfile?.FullName.toUpperCase()
                    },
                    { 
                      label: 'Email Address', 
                      icon: FaEnvelope, 
                      field: 'Email',
                      value: userProfile?.Email.toUpperCase() 
                    },
                    { 
                      label: 'Phone Number', 
                      icon: FaPhone, 
                      field: 'Phone',
                      value: userProfile?.Phone || 'N/A' 
                    }
                  ].map((item, index) => (
                    <div 
                      key={item.field}
                      className="space-y-2 animate-fade-in-up"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <label className="block text-sm font-medium text-[#64748b] mb-2 flex items-center">
                        <item.icon className="mr-2 text-[#6A89A7]" /> {item.label}
                      </label>
                      {isEditing ? (
                        <input
                          type={item.field === 'Email' ? 'email' : item.field === 'Phone' ? 'tel' : 'text'}
                          value={editForm[item.field] || ''}
                          onChange={(e) => handleInputChange(item.field, e.target.value)}
                          className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#BDDDFC] focus:border-transparent transition-all duration-300 transform hover:scale-[1.01]"
                        />
                      ) : (
                        <div className="p-4 bg-[#F9FAFB] rounded-lg border border-[#E2E8F0] transform transition-all duration-300 hover:scale-[1.01]">
                          <p className="text-[#384959] font-medium">{item.value}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Info Card */}
              <div className="bg-white rounded-2xl shadow-xl p-8 animate-slide-in-right animation-delay-400">
                <h3 className="text-2xl font-bold text-[#384959] mb-6 flex items-center gap-2">
                  <FaBuilding className="text-[#6A89A7]" />
                  Additional Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { 
                      label: 'Role', 
                      icon: FaBuilding, 
                      value: userProfile?.Role.toUpperCase() || 'User',
                      isBadge: true
                    },
                    { 
                      label: 'Account Created', 
                      icon: FaCalendar, 
                      value: formatDate(userProfile?.CreatedAt).toUpperCase()
                    },
                    { 
                      label: 'Last Login', 
                      icon: FaClock, 
                      value: formatDate(userProfile?.LastLogin).toUpperCase()
                    },
                    { 
                      label: 'Account Status', 
                      icon: FaLock, 
                      value: userProfile?.IsActive ? 'Active' : 'Inactive',
                      isBadge: true
                    }
                  ].map((item, index) => (
                    <div 
                      key={item.label}
                      className="space-y-2 animate-fade-in-up"
                      style={{ animationDelay: `${index * 100 + 200}ms` }}
                    >
                      <label className="block text-sm font-medium text-[#64748b] mb-2 flex items-center">
                        <item.icon className="mr-2 text-[#6A89A7]" /> {item.label}
                      </label>
                      <div className="flex items-center space-x-3 p-4 bg-[#F9FAFB] rounded-lg border border-[#E2E8F0] transform transition-all duration-300 hover:scale-[1.01]">
                        <div className="w-10 h-10 rounded-full bg-[#BDDDFC] flex items-center justify-center">
                          <item.icon className="text-[#6A89A7]" />
                        </div>
                        <div className="flex-1">
                          {item.isBadge ? (
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                              item.label === 'Role' ? getRoleColor(item.value) : getStatusColor(item.value === 'Active')
                            }`}>
                              {item.value}
                            </span>
                          ) : (
                            <p className="text-[#384959] font-medium">{item.value}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Account Summary Card */}

              {/* SMTP Settings Section (Admin Only) */}
              {userProfile && userProfile.Role && userProfile.Role.toLowerCase() === 'admin' && (
                <div className="bg-white rounded-2xl shadow-xl p-8 animate-fade-in-up animation-delay-800">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-[#384959] flex items-center gap-2">
                      <FaServer className="text-[#6A89A7]" />
                      SMTP Email Settings
                    </h3>
                    {smtpMode === 'view' && smtpSettings && (
                      <button
                        onClick={() => { setSmtpForm(smtpSettings); setSmtpMode('edit'); setSmtpError(''); setSmtpSuccess(''); }}
                        className="flex items-center space-x-2 bg-[#6A89A7] hover:bg-[#384959] text-white px-4 py-2 rounded-lg transition-all duration-300 transform hover:scale-105"
                      >
                        <FaEdit />
                        <span>Edit Settings</span>
                      </button>
                    )}
                  </div>
                  
                  {smtpError && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center animate-pulse">
                      <FaExclamationTriangle className="mr-2" />
                      {smtpError}
                    </div>
                  )}
                  
                  {smtpSuccess && (
                    <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center animate-bounce">
                      <FaCheckCircle className="mr-2" />
                      {smtpSuccess}
                    </div>
                  )}
                  
                  {smtpLoading && (
                    <div className="flex items-center gap-2 mb-6">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#6A89A7]"></div>
                      <span className="text-[#384959] font-medium">Loading SMTP settings...</span>
                    </div>
                  )}
                  
                  {/* SMTP Settings Content */}
                  {smtpMode === 'view' && smtpSettings && !smtpLoading && (
                    <div className="mb-6 animate-fade-in">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        {[
                          { icon: FaServer, label: 'SMTP Host', value: smtpSettings.host },
                          { icon: FaKey, label: 'SMTP Port', value: smtpSettings.port },
                          { icon: FaUser, label: 'SMTP User', value: smtpSettings.user },
                          { icon: FaAt, label: 'From Email', value: smtpSettings.from }
                        ].map((item, index) => (
                          <div 
                            key={item.label}
                            className="flex items-center gap-3 p-4 bg-[#F9FAFB] rounded-lg border border-[#E2E8F0] transform transition-all duration-300 hover:scale-[1.02] animate-fade-in-up"
                            style={{ animationDelay: `${index * 100}ms` }}
                          >
                            <div className="w-10 h-10 rounded-full bg-[#BDDDFC] flex items-center justify-center">
                              <item.icon className="text-[#6A89A7]" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-[#64748b]">{item.label}</p>
                              <p className="font-medium text-[#384959] uppercase">{item.value}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-3">
                        <button
                          className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-all duration-300 transform hover:scale-105"
                          onClick={async () => {
                            setSmtpLoading(true);
                            setSmtpError('');
                            setSmtpSuccess('');
                            try {
                              const res = await fetch(`${API_URL}/smtp-settings`, { method: 'DELETE',headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        } });
                              const data = await res.json();
                              if (res.ok && data.success) {
                                setSmtpSuccess('SMTP settings deleted successfully!');
                                setSmtpSettings(null);
                                setSmtpForm({ host: '', port: '', user: '', pass: '', from: '' });
                                setSmtpMode('add');
                              } else {
                                setSmtpError(data.message || 'Failed to delete SMTP settings');
                              }
                            } catch (err) {
                              setSmtpError('Failed to delete SMTP settings');
                            } finally {
                              setSmtpLoading(false);
                            }
                          }}
                        >
                          <FaTimes /> Delete Settings
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Add SMTP Button */}
                  {smtpMode === 'add' && !smtpLoading && (
                    <div className="mb-6 animate-fade-in">
                      <button
                        className="flex items-center space-x-2 bg-[#6A89A7] hover:bg-[#384959] text-white px-4 py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
                        onClick={() => { setSmtpForm({ host: '', port: '', user: '', pass: '', from: '' }); setSmtpMode('edit'); setSmtpError(''); setSmtpSuccess(''); }}
                      >
                        <FaPaperPlane /> Add SMTP Settings
                      </button>
                    </div>
                  )}
                  
                  {/* SMTP Form */}
                  {smtpMode === 'edit' && !smtpLoading && (
                    <form
                      className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#F9FAFB] p-6 rounded-xl mb-6 border border-[#E2E8F0] shadow-sm animate-fade-in"
                      onSubmit={e => { e.preventDefault(); handleSmtpSave(); }}
                    >
                      {[
                        { label: 'SMTP Host', field: 'host', type: 'text' },
                        { label: 'SMTP Port', field: 'port', type: 'text' },
                        { label: 'SMTP User', field: 'user', type: 'text' },
                        { label: 'SMTP Password', field: 'pass', type: 'password' }
                      ].map((item, index) => (
                        <div 
                          key={item.field}
                          className="animate-fade-in-up"
                          style={{ animationDelay: `${index * 100}ms` }}
                        >
                          <label className="block text-sm font-medium text-[#64748b] mb-2">{item.label}</label>
                          <input
                            type={item.type}
                            className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#BDDDFC] transition-all duration-300 transform hover:scale-[1.01]"
                            value={smtpForm[item.field]}
                            onChange={e => handleSmtpInputChange(item.field, e.target.value)}
                            required
                          />
                        </div>
                      ))}
                      
                      <div className="md:col-span-2 animate-fade-in-up" style={{ animationDelay: '400ms' }}>
                        <label className="block text-sm font-medium text-[#64748b] mb-2">From Email</label>
                        <input
                          type="email"
                          className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#BDDDFC] transition-all duration-300 transform hover:scale-[1.01]"
                          value={smtpForm.from}
                          onChange={e => handleSmtpInputChange('from', e.target.value)}
                          required
                        />
                      </div>
                      
                      <div className="md:col-span-2 flex gap-3 mt-2 animate-fade-in-up" style={{ animationDelay: '500ms' }}>
                        <button
                          type="submit"
                          className="flex items-center space-x-2 bg-[#10B981] hover:bg-[#059669] text-white px-4 py-2 rounded-lg transition-all duration-300 transform hover:scale-105"
                          disabled={smtpLoading}
                        >
                          <FaSave /> Save Settings
                        </button>
                        <button
                          type="button"
                          className="flex items-center space-x-2 bg-gray-400 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-all duration-300 transform hover:scale-105"
                          onClick={() => { setSmtpForm(smtpSettings || { host: '', port: '', user: '', pass: '', from: '' }); setSmtpMode(smtpSettings ? 'view' : 'add'); setSmtpError(''); setSmtpSuccess(''); }}
                          disabled={smtpLoading}
                        >
                          <FaTimes /> Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fade-in-down {
          from { 
            opacity: 0;
            transform: translateY(-20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in-up {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slide-in-left {
          from { 
            opacity: 0;
            transform: translateX(-50px);
          }
          to { 
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slide-in-right {
          from { 
            opacity: 0;
            transform: translateX(50px);
          }
          to { 
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes gradient-x {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes gradient-flow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
        }
        
        .animate-fade-in-down {
          animation: fade-in-down 0.6s ease-out forwards;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }
        
        .animate-slide-in-left {
          animation: slide-in-left 0.8s ease-out forwards;
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.8s ease-out forwards;
        }
        
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
        
        .animate-gradient-flow {
          background-size: 200% 200%;
          animation: gradient-flow 8s ease infinite;
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        .animate-bounce {
          animation: bounce 1s infinite;
        }
        
        .animation-delay-100 {
          animation-delay: 0.1s;
        }
        
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        
        .animation-delay-300 {
          animation-delay: 0.3s;
        }
        
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
        
        .animation-delay-500 {
          animation-delay: 0.5s;
        }
        
        .animation-delay-600 {
          animation-delay: 0.6s;
        }
        
        .animation-delay-800 {
          animation-delay: 0.8s;
        }
      `}</style>
    </div>
  );
};

export default Profile;
